import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Home, Users, LayoutGrid, Radar as RadarIcon, User, Bell, Lock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import AIAssistant from "@/components/AIAssistant";
import { KWordmark } from "@/components/KLogo";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/app", icon: Home, label: "Início", end: true },
  { to: "/app/comunidade", icon: Users, label: "Comunidade" },
  { to: "/app/servicos", icon: LayoutGrid, label: "Serviços" },
  { to: "/app/radar", icon: RadarIcon, label: "Radar" },
  { to: "/app/perfil", icon: User, label: "Perfil" },
];

export default function ResidentLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["notif-count"], queryFn: async () => (await api.get("/notifications")).data, refetchInterval: 20000 });
  const unread = data?.unread || 0;
  const locked = user?.role === "resident" && user?.status !== "approved" && loc.pathname !== "/app";

  return (
    <div className="min-h-[100dvh] bg-background">
      {/* Top bar (mobile + desktop) */}
      <header className="sticky top-0 z-30 glass-strong border-b border-white/5">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <KWordmark size={28} />
          <button onClick={() => nav("/app/notificacoes")} data-testid="notif-bell" className="relative p-2 rounded-full hover:bg-white/10 transition-colors">
            <Bell className="w-5 h-5" />
            {unread > 0 && (
              <span data-testid="notif-badge" className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full brand-gradient ring-2 ring-background" />
            )}
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-4 pb-28">
        {locked ? (
          <div data-testid="locked-notice" className="flex flex-col items-center justify-center text-center py-24 px-6">
            <div className="w-14 h-14 rounded-2xl glass flex items-center justify-center mb-4"><Lock className="w-6 h-6 text-yellow-400" /></div>
            <p className="font-display font-semibold">Acesso em análise</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-xs">Esta área será liberada assim que a administração aprovar seu cadastro.</p>
            <button onClick={() => nav("/app")} className="mt-4 rounded-full brand-gradient px-5 py-2.5 text-white text-sm font-semibold active:scale-95 transition-transform">Voltar ao início</button>
          </div>
        ) : (
          <Outlet />
        )}
      </main>

      <AIAssistant />

      {/* Bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-30 glass-strong border-t border-white/10 pb-safe">
        <div className="max-w-2xl mx-auto px-2 grid grid-cols-5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 text-[11px] transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className={`p-1 rounded-lg ${isActive ? "bg-primary/15" : ""}`}>
                    <n.icon className="w-5 h-5" />
                  </div>
                  <span className="font-medium">{n.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
